import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { PlannerService } from '../../../service/api/planner-service-v2';
import { normalizeSavePlanWorkTicketFields } from '../../../service/api/planner-save-plan-body';
const { PROGRAM_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'planner_dashboard';
// Feature flag to control whether we should immediately re-fetch planner data
// after a successful delete. When the backend does not persist deletes instantly
// (or the delete identifier differs from the fetch identifier), re-fetching can
// re-introduce the just-removed item. Keeping this false relies on our local
// state update which is already done in deleteByWorkTicketId.fulfilled.
const REFRESH_AFTER_DELETE = false;
export const INITIAL_STATE = {
  data: {
    channels: [],
    programs: [],
    elements: [],
  },
  // Track deleted program identifiers for this session so auto-refresh
  // or re-fetch does not re-introduce the deleted items if the backend
  // still returns them.
  deletedProgramIds: [],
  // Extra signature keys (like title/description) to guard against
  // servers that return different identifiers between endpoints
  deletedProgramKeys: [],
  deletedElementIds: [],
  loading: false,
  error: null,
  filters: {
    start: '',
    end: '',
     searchText: '',
    status: ''   // new
  },
  fetchWorkTicketPending: false,
  completionStatus: {}, // Map of ProcessId to completion percentage
  completionStatusLoading: false,
};

const handleResponse = async (promise, rejectWithValue) => {
  try {
    const response = await promise;
    if (response.status === 200) {
      return response;
    } else {
      return rejectWithValue(response.errorMessage?.message || 'Failed to process request');
    }
  } catch (error) {
    return rejectWithValue(error.message || 'Failed to process request');
  }
};

// Utility comparator used across reducers
const matchesId = (p, id) => {
  const pid = String(id);
  return [
    p.id,
    p.workTicketNumber,
    p.workTicketNo,
    p.workTicket,
    p.processIdExt,
  ].some(v => v !== undefined && v !== null && String(v) === pid);
};

const collectKeys = (obj) => {
  if (!obj) return [];
  const keys = [
    obj.id,
    obj.workTicketNumber,
    obj.workTicketNo,
    obj.workTicket,
    obj.processIdExt,
    obj.title,
    obj.description,
  ]
    .filter(v => v !== undefined && v !== null)
    .map(v => String(v));
  return Array.from(new Set(keys));
};

export const fetchPlanner = createAsyncThunk(
  'planner/fetchPlanner',
  async (filters, { rejectWithValue }) => {
    const channelResponse = await handleResponse(PlannerService.getAllChannel(), rejectWithValue);
    const taskResponse = await handleResponse(PlannerService.getAllTask(), rejectWithValue);
    
    return [channelResponse.response || [], taskResponse.response];
  },
);

export const savePlan = createAsyncThunk('planner/savePlan', async (data, { rejectWithValue }) => {
  const payload = { ...data };
  normalizeSavePlanWorkTicketFields(payload);

  const userId = localStorage.getItem('UserId');
  if (userId != null && String(userId).trim() !== '') {
    payload.userId = String(userId).trim();
  } else {
    delete payload.userId;
    delete payload.UserId;
  }

  const idStr = payload.id != null ? String(payload.id).trim() : '';
  if (!/^\d+$/.test(idStr)) {
    const ticket = String(payload.title ?? '').trim();
    if (ticket) {
      payload.id = `W${ticket}`;
    }
  }

  if (payload.since != null) {
    payload.since = String(payload.since).replace(' ', 'T');
  }
  if (payload.till != null) {
    payload.till = String(payload.till).replace(' ', 'T');
  }

  return handleResponse(PlannerService.savePlan(payload), rejectWithValue);
});

export const updatePlan = createAsyncThunk(
  'planner/updatePlan',
  async (data, { rejectWithValue }) => {
    const payload = { ...data };
    normalizeSavePlanWorkTicketFields(payload);

    const userId = localStorage.getItem('UserId');
    if (userId != null && String(userId).trim() !== '') {
      payload.userId = String(userId).trim();
    } else {
      delete payload.userId;
      delete payload.UserId;
    }

    return handleResponse(PlannerService.savePlan(payload), rejectWithValue);
  },
);

export const updateProcess = createAsyncThunk(
  'planner/updateProcess',
  async (data, { rejectWithValue }) => {
    return handleResponse(PlannerService.updateProcess(data), rejectWithValue);
  },
);

export const fetchByWorkTicketId = createAsyncThunk(
  'planner/fetchByWorkTicketId',
  async (id, { rejectWithValue }) => {
    return handleResponse(PlannerService.getByWorkTicketId(id), rejectWithValue);
  },
);

export const deleteByWorkTicketId = createAsyncThunk(
  'planner/deleteByWorkTicketId',
  async (id, { rejectWithValue }) => {
    return handleResponse(PlannerService.deleteByWorkTicketId(id), rejectWithValue);
  },
);

export const fetchProcessCompletionStatus = createAsyncThunk(
  'planner/fetchProcessCompletionStatus',
  async (_, { rejectWithValue }) => {
    return handleResponse(PlannerService.getProcessCompletionStatus(), rejectWithValue);
  },
);

export const { reducer, actions } = createSlice({
    //new code(status filter)
//const slice = createSlice({
  name: STORE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    // setFromDate: (state, action) => {
    //   state.filters.fromDate = action.payload;
    // },
    // setToDate: (state, action) => {
    //   state.filters.toDate = action.payload;
    // },

    //new
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    removeElement: (state, action) => {
      const id = String(action.payload);
      state.data.elements = state.data.elements.map(element =>
        String(element.id) === id ? { ...element, deleted: true } : element,
      );
      if (!state.deletedElementIds?.includes(id)) {
        state.deletedElementIds?.push(id);
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPlanner.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlanner.fulfilled, (state, action) => {
        state.loading = false;
        state.data.channels = action.payload[0] || [];
        const incomingPrograms = action.payload[1]?.programs || [];
        // Filter out programs that were deleted during this session
        if (state.deletedProgramIds?.length > 0 || state.deletedProgramKeys?.length > 0) {
          state.data.programs = incomingPrograms.filter(p => {
            const candidateKeys = collectKeys(p);
            const idExcluded = state.deletedProgramIds.some(id => matchesId(p, id));
            const keyExcluded = candidateKeys.some(k => state.deletedProgramKeys?.includes(k));
            return !(idExcluded || keyExcluded);
          });
        } else {
          state.data.programs = incomingPrograms;
        }
        const incomingElements = action.payload[1]?.elements || [];
        state.data.elements = incomingElements
          .filter(element => !state.deletedElementIds?.includes(String(element.id)))
          .map(element => ({
            ...element,
            duration: '120',
            deleted: false,
          }));
      })
      .addCase(fetchPlanner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(savePlan.fulfilled, (state, action) => {
        if (action.payload?.response) {
          state.data.programs = [...state.data.programs, action.payload.response];
          // If a plan is created with an id that was previously deleted,
          // allow it to reappear by removing from deleted list
          const newId = action.payload.response.id;
          state.deletedProgramIds = state.deletedProgramIds.filter(id => String(id) !== String(newId));
        }
      })
      .addCase(fetchByWorkTicketId.pending, state => {
        state.fetchWorkTicketPending = true;
      })
      .addCase(fetchByWorkTicketId.fulfilled, state => {
        state.fetchWorkTicketPending = false;
      })
      .addCase(fetchByWorkTicketId.rejected, state => {
        state.fetchWorkTicketPending = false;
      })
      .addCase(deleteByWorkTicketId.fulfilled, (state, action) => {
        // Get the program ID from the action payload (the original request parameter)
        const programId = action.meta.arg;
        const response = action.payload?.response;
        const deletedProgram = state.data.programs.find(p => matchesId(p, programId));

        // Ensure arrays are initialized
        if (!Array.isArray(state.data.programs)) state.data.programs = [];
        if (!Array.isArray(state.data.elements)) state.data.elements = [];
        if (!Array.isArray(state.deletedProgramIds)) state.deletedProgramIds = [];
        if (!Array.isArray(state.deletedProgramKeys)) state.deletedProgramKeys = [];
        if (!Array.isArray(state.deletedElementIds)) state.deletedElementIds = [];

        // Update programs and elements from the API response if available
        if (response) {
          if (response.programs && Array.isArray(response.programs)) {
            state.data.programs = response.programs;
          }
          if (response.elements && Array.isArray(response.elements)) {
            const incomingElements = response.elements;
            state.data.elements = incomingElements
              .filter(element => !state.deletedElementIds?.includes(String(element.id)))
              .map(element => ({
                ...element,
                duration: element.duration ? String(element.duration) : '120',
                deleted: false,
              }));
          }
        } else if (deletedProgram) {
          // Fallback: If no response, manually convert program to element
          // Create an element from the deleted program
          const elementId = deletedProgram.workTicketNumber || deletedProgram.workTicketNo || deletedProgram.id;
          const newElement = {
            id: elementId,
            title: deletedProgram.title || deletedProgram.workTicketNumber || deletedProgram.workTicketNo || 'Deleted Item',
            description: deletedProgram.description || deletedProgram.title || '',
            duration: '120',
            deleted: false,
            originalProgram: deletedProgram,
            createdAt: new Date().toISOString()
          };
          
          // Update or add element
          const existingElementIndex = state.data.elements.findIndex(e => String(e.id) === String(elementId));
          if (existingElementIndex >= 0) {
            // Update existing element (restore it if it was deleted)
            state.data.elements[existingElementIndex] = {
              ...state.data.elements[existingElementIndex],
              ...newElement,
              deleted: false, // Ensure it's not marked as deleted
            };
          } else {
            // Add new element
            state.data.elements.push(newElement);
          }
          
          // Remove from programs (match by multiple possible identifiers)
          state.data.programs = state.data.programs.filter(p => !matchesId(p, programId));
        }

        // Remember this deletion for the rest of the session to prevent
        // auto-refresh from bringing the item back if the backend still
        // returns it.
        if (deletedProgram) {
          if (!state.deletedProgramIds.some(id => String(id) === String(programId))) {
            state.deletedProgramIds.push(String(programId));
          }

          // Also store additional keys (title/description/etc.) to guard against
          // different identifiers being returned by fetch.
          const keysToStore = collectKeys(deletedProgram);
          keysToStore.forEach(k => {
            if (!state.deletedProgramKeys.includes(k)) {
              state.deletedProgramKeys.push(k);
            }
          });

          // Remove from deletedElementIds so the element can appear in task bar
          const elementId = deletedProgram.workTicketNumber || deletedProgram.workTicketNo || deletedProgram.id;
          if (elementId) {
            state.deletedElementIds = state.deletedElementIds.filter(id => String(id) !== String(elementId));
          }
        }
      })
      .addCase(updateProcess.fulfilled, (state, action) => {
        if (action.payload?.response) {
          const updatedProcess = action.payload.response;
          state.data.programs = state.data.programs.map(program =>
            program.id === updatedProcess.id ? updatedProcess : program,
          );
        }
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        if (action.payload?.response) {
          const updatedPlan = action.payload.response;
          state.data.programs = state.data.programs.map(program =>
            program.id === updatedPlan.id ? updatedPlan : program,
          );
        }
      })
      .addCase(fetchProcessCompletionStatus.pending, state => {
        state.completionStatusLoading = true;
      })
      .addCase(fetchProcessCompletionStatus.fulfilled, (state, action) => {
        state.completionStatusLoading = false;
        // Convert array to object map for easy lookup by processId
        const completionMap = {};
        const responseData = action.payload?.response || action.payload?.data || (Array.isArray(action.payload) ? action.payload : []);
        
        if (Array.isArray(responseData) && responseData.length > 0) {
          responseData.forEach((item) => {
            const processId = item.processId;
            if (processId !== undefined && processId !== null) {
              const processIdKey = String(processId);
              completionMap[processIdKey] = {
                plannedQuantity: item.plannedQuantity || 0,
                inventoryCount: item.inventoryCount || 0,
                completionPercentage: item.completionPercentage || 0,
              };
            }
          });
        }
        
        state.completionStatus = completionMap;
      })
      .addCase(fetchProcessCompletionStatus.rejected, (state, action) => {
        state.completionStatusLoading = false;
        state.completionStatus = {};
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(fetchPlanner.rejected, savePlan.rejected, updateProcess.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});


redux.listener.startListening({
  matcher: isAnyOf(updatePlan.fulfilled),
  effect: (action, listenerApi) => {
    // Refresh the planner grid after drag-and-drop updates
    const state = listenerApi.getState();
    const filters = state.planner.filters;
    listenerApi.dispatch(
      fetchPlanner({
        searchText: filters.searchText,
        status: filters.status,
        start: filters.start,
        end: filters.end,
      }),
    );
  },
});

if (REFRESH_AFTER_DELETE) {
  redux.listener.startListening({
    matcher: isAnyOf(deleteByWorkTicketId.fulfilled),
    effect: (action, listenerApi) => {
      const state = listenerApi.getState();
      const filters = state.planner.filters;
      listenerApi.dispatch(
        fetchPlanner({
          searchText: filters.searchText,
          status: filters.status,
          start: filters.start,
          end: filters.end,
        }),
      );
    },
  });
}

redux.listener.startListening({
  matcher: isAnyOf(fetchByWorkTicketId.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(showModal(PROGRAM_ADD_EDIT, action.payload.response));
  },
});



// export const actions = slice.actions;
// export const { setFilters, setSearchText, removeElement } = slice.actions;
// const reducer = slice.reducer;

// redux.injectReducer(STORE_KEY, reducer);

// export default reducer;

redux.listener.startListening({
  matcher: isAnyOf(updateProcess.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(PROGRAM_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Plan updated successfully',
        type: 'success',
      }),
    );
    // Refresh the planner grid after modal updates
    const state = listenerApi.getState();
    const filters = state.planner.filters;
    listenerApi.dispatch(
      fetchPlanner({
        searchText: filters.searchText,
        status: filters.status,
        start: filters.start,
        end: filters.end,
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);
